import { Link } from '@/components/Link';
import type { Seller } from '../../features/sellers/model';
import type { RawCatalogEntry } from '../../lib/catalogMap';
import { SellerApis } from './SellerApis';
import { SellerPhoto } from './SellerPhoto';

const NOTE = 'mt-9 text-15 leading-relaxed text-text-2';

/** The sellers, each with its photo, name and a short description linking to
 *  its own page (an anonymous seller only its assigned name), and the endpoints
 *  it serves; or the state when there are none.
 *  An absent list means the gateway could not be reached, which is not the same
 *  claim as "no sellers". */
export const SellerList = ({
  sellers,
  catalog,
}: {
  sellers: Seller[] | undefined;
  catalog: RawCatalogEntry[];
}) => {
  if (!sellers) {
    return (
      <p className={NOTE}>
        The list could not be loaded right now. Refresh the page, or email{' '}
        <a href="mailto:support@getanyapi.com">support@getanyapi.com</a> for the current list.
      </p>
    );
  }
  if (sellers.length === 0) {
    return (
      <p className={NOTE}>
        There are no marketplace sellers yet, so no request is fulfilled by one.
      </p>
    );
  }
  return (
    <ul className="mt-9 divide-y divide-rule border-y border-rule">
      {sellers.map((seller) => (
        <li key={seller.slug} className="flex gap-4 py-5">
          {seller.anonymous ? null : <SellerPhoto src={seller.photoUrl} name={seller.name} className="size-14" />}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h2 className="text-16 font-medium text-text">
              <Link to={`/sellers/${seller.slug}`} className="hover:text-accent hover:underline">
                {seller.name}
              </Link>
            </h2>
            {seller.anonymous ? null : (
              <p className="line-clamp-2 text-14 leading-relaxed text-text-2">{seller.description}</p>
            )}
            <SellerApis skus={seller.skus} catalog={catalog} />
          </div>
        </li>
      ))}
    </ul>
  );
};
